import { useTranslation } from 'next-i18next';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { AccordionV2 } from '@restworld/ui-ds-base';
import { cn } from '@restworld/utils-common';

import { useReferralCredits } from './use-referral-credits';

const FAQ_KEYS = ['faq_1', 'faq_2', 'faq_3'] as const;

/**
 * FAQ accordion section with 3 collapsible question/answer items.
 */
export function ReferralFaqSection() {
  const { t } = useTranslation('referral-page');
  const { inviter } = useReferralCredits();

  return (
    <SectionContainer>
      <SectionTitle>{t('faq_title')}</SectionTitle>

      <FaqList>
        {FAQ_KEYS.map((key) => (
          <FaqItem key={key}>
            <AccordionV2 open={false}>
              <AccordionV2.Button
                IconComponent={ChevronDownIcon}
                wrapperStyle="w-full px-4 py-4 gap-4 text-lg font-semibold text-[#1C1C33]"
              >
                <span>{t(`${key}_question`)}</span>
              </AccordionV2.Button>
              <AccordionV2.Panel>
                <AnswerContent>{t(`${key}_answer`, { inviter })}</AnswerContent>
              </AccordionV2.Panel>
            </AccordionV2>
          </FaqItem>
        ))}
      </FaqList>
    </SectionContainer>
  );
}

export default ReferralFaqSection;

// Layout Components
const SectionContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center w-full py-10 px-4 lg:px-20 gap-10', className)} {...props} />
);

const SectionTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h2 className={cn('text-xl font-semibold text-[#1C1C33] leading-[1.21]', className)} {...props} />
);

const FaqList = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 w-full', className)} {...props} />
);

const FaqItem = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('bg-[#F5F5F6] rounded-2xl overflow-hidden', className)} {...props} />
);

const AnswerContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('px-4 pb-4 text-sm text-[#666676] leading-relaxed', className)} {...props} />
);
